import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Screen from '../demo/Screen';
import { readCapturedDetails } from '../demo/capturedDetails';
import { requestDemo } from './start/services/signupService';
import { ClockGlyph, DocGlyph, PeopleGlyph, PhoneGlyph } from '../mocks/TileGlyphs';
import EndScreen from './nextstep/EndScreen';
import './NextStep.css';

const DEMO_POINTS = [
  { key: 'load', icon: DocGlyph, tone: 'blue', text: 'We load a few of your anonymised reports' },
  {
    key: 'see',
    icon: PhoneGlyph,
    tone: 'green',
    text: 'You see them as Smart Reports, on the app and on WhatsApp',
  },
  { key: 'size', icon: PeopleGlyph, tone: 'purple', text: 'We size it for your volume and pick a plan together' },
];

const VOLUMES = ['Under 1,000', '1,000–5,000', '5,000–20,000', '20,000+'];

const PLAN_OPTIONS = [
  { value: '', label: 'Not sure yet' },
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'pro', label: 'Pro' },
];
const VALID_PLANS = PLAN_OPTIONS.map((p) => p.value).filter(Boolean);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SEND_DELAY_MS = 450; // brief "Sending…" state; skipped under reduced motion
const FIELD_ORDER = ['name', 'email', 'lab'];

export function validateDemoRequest(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = 'Please enter your name';
  if (!EMAIL_RE.test(values.email.trim())) errors.email = 'Enter a valid work email';
  if (!values.lab.trim()) errors.lab = 'Please enter your lab or hospital name';
  return errors;
}

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function TextField({
  field,
  label,
  type = 'text',
  autoComplete,
  required,
  value,
  error,
  onChange,
  inputRef,
}) {
  const id = `nsd-${field}`;
  return (
    <div className="nsd-field">
      <label htmlFor={id} className="nsd-label">
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        name={field}
        type={type}
        className="nsd-input"
        autoComplete={autoComplete}
        required={required}
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        data-trapezium-id={`next-step.book-demo.input-${field}`}
      />
      {error && (
        <p id={`${id}-error`} className="nsd-error">
          {error}
        </p>
      )}
    </div>
  );
}

function DemoForm({ status, setStatus, focusOnOpen }) {
  const [params] = useSearchParams();
  const planParam = params.get('plan');
  const [values, setValues] = useState(() => ({
    name: '',
    email: '',
    lab: '',
    phone: '',
    volume: VOLUMES[0],
    plan: VALID_PLANS.includes(planParam) ? planParam : '',
  }));
  const [errors, setErrors] = useState({});
  const refs = { name: useRef(null), email: useRef(null), lab: useRef(null) };
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  // Focus the first field when the visitor chooses "Book a live demo" (not on arrival).
  const nameRef = refs.name;
  useEffect(() => {
    if (focusOnOpen) nameRef.current?.focus();
  }, [focusOnOpen, nameRef]);

  function update(field, value) {
    const next = { ...values, [field]: value };
    setValues(next);
    // Clear a field's error as soon as it is fixed; new errors only appear on submit.
    if (errors[field] && !validateDemoRequest(next)[field]) {
      setErrors(({ [field]: _fixed, ...rest }) => rest);
    }
  }

  function onSubmit(event) {
    event.preventDefault();
    if (status !== 'idle') return;
    const found = validateDemoRequest(values);
    setErrors(found);
    const first = FIELD_ORDER.find((f) => found[f]);
    if (first) {
      refs[first].current?.focus();
      return;
    }
    if (prefersReducedMotion()) {
      setStatus('submitted');
      return;
    }
    setStatus('sending');
    timer.current = setTimeout(() => setStatus('submitted'), SEND_DELAY_MS);
  }

  const errorList = FIELD_ORDER.filter((f) => errors[f]);
  const sending = status === 'sending';

  return (
    <form id="nsd-form" className="card nsd-form" noValidate onSubmit={onSubmit} aria-labelledby="nsd-form-title">
      <h2 id="nsd-form-title" className="nsd-form__title">
        Book your demo
      </h2>
      <div role="alert" className="nsd-summary">
        {errorList.length > 0 && (
          <>
            <p className="nsd-summary__title">
              Please check {errorList.length === 1 ? 'one field' : 'these fields'}:
            </p>
            <ul>
              {errorList.map((f) => (
                <li key={f}>{errors[f]}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="nsd-grid">
        <TextField
          field="name"
          label="Your name"
          autoComplete="name"
          required
          value={values.name}
          error={errors.name}
          onChange={update}
          inputRef={refs.name}
        />
        <TextField
          field="email"
          label="Work email"
          type="email"
          autoComplete="email"
          required
          value={values.email}
          error={errors.email}
          onChange={update}
          inputRef={refs.email}
        />
        <TextField
          field="lab"
          label="Lab or hospital name"
          autoComplete="organization"
          required
          value={values.lab}
          error={errors.lab}
          onChange={update}
          inputRef={refs.lab}
        />
        <TextField
          field="phone"
          label="Phone (optional)"
          type="tel"
          autoComplete="tel"
          value={values.phone}
          onChange={update}
        />
        <div className="nsd-field">
          <label htmlFor="nsd-volume" className="nsd-label">
            Reports per month
          </label>
          <select
            id="nsd-volume"
            className="nsd-input"
            value={values.volume}
            onChange={(e) => update('volume', e.target.value)}
            data-trapezium-id="next-step.book-demo.select-volume"
          >
            {VOLUMES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="nsd-field">
          <label htmlFor="nsd-plan" className="nsd-label">
            Preferred plan
          </label>
          <select
            id="nsd-plan"
            className="nsd-input"
            value={values.plan}
            onChange={(e) => update('plan', e.target.value)}
            data-trapezium-id="next-step.book-demo.select-plan"
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p.value || 'none'} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="submit"
        className="btn btn--primary nsd-submit"
        disabled={sending}
        data-trapezium-id="next-step.book-demo.submit-request"
      >
        {sending ? 'Sending…' : 'Request my demo'}
      </button>
      <p className="nsd-privacy">Demo site: nothing is sent.</p>
    </form>
  );
}

const DETAIL_ROWS = [
  ['name', 'Name'],
  ['organisation', 'Organisation'],
  ['email', 'Work email'],
  ['phone', 'Mobile'],
];

/*
 * The sign-up at /start already captured who the visitor is (sessionStorage
 * ng.signup, src/demo/capturedDetails.js). "Book a live demo" then confirms
 * those details instead of re-asking for them: read-only, inside the private
 * section, with "Not you? Edit" back to /start. Confirming saves the request
 * for a visitor verified on this browser (sai's finishLead + the deployed
 * new-lead email, signupService.requestDemo); unverified, nothing is sent.
 */
function CapturedDetails({ details, status, setStatus, focusOnOpen }) {
  const [error, setError] = useState('');
  const confirmRef = useRef(null);

  useEffect(() => {
    if (focusOnOpen) confirmRef.current?.focus();
  }, [focusOnOpen]);

  async function onConfirm() {
    if (status !== 'idle') return;
    setError('');
    setStatus('sending');
    try {
      await requestDemo();
      setStatus('submitted');
    } catch {
      setError('We couldn’t send your request. Check your connection and try again.');
      setStatus('idle');
    }
  }

  return (
    <div id="nsd-confirm" className="card nsd-form nsd-confirm">
      <h2 className="nsd-form__title">Confirm your details</h2>
      <dl className="nsd-details">
        {DETAIL_ROWS.filter(([key]) => details[key]).map(([key, label]) => (
          <div key={key} className="nsd-details__row">
            <dt>{label}</dt>
            <dd>{details[key]}</dd>
          </div>
        ))}
      </dl>
      <Link to="/start" className="nsd-confirm__edit" data-trapezium-id="next-step.book-demo.edit-details">
        Not you? Edit
      </Link>
      <p className="nsd-confirm__note">A 30-minute call on a few of your own reports.</p>
      <button
        ref={confirmRef}
        type="button"
        className="btn btn--primary nsd-submit"
        disabled={status === 'sending'}
        onClick={onConfirm}
        data-trapezium-id="next-step.book-demo.confirm-request"
      >
        {status === 'sending' ? 'Sending…' : 'Confirm demo request'}
      </button>
      {error && (
        <p className="nsd-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/*
 * Step 22 · the closing flow. After "What happens in the demo" the visitor
 * picks "Book a live demo" (the short form opens below; the choice stays) or
 * "Skip for now". Either way it ends on the game-style end screen
 * (nextstep/EndScreen.jsx), which takes over the page: the intro becomes
 * sr-only and the cards hide. The choice and the form stay mounted (hidden)
 * once the flow ends, and the end screen stays mounted (hidden) once shown, so
 * no control is ever unmounted by its own click. Its role="status" element
 * carries the post-condition: data-trapezium-state "submitted" or "skipped".
 * After a skip, its "Book a live demo" (end-book) reopens the booking. When the
 * sign-up captured the visitor's details, "Book a live demo" opens a short
 * confirm view (CapturedDetails) instead of the form.
 */
export default function NextStep() {
  const [status, setStatus] = useState('idle'); // idle | sending | submitted | skipped
  const [params] = useSearchParams();
  // Arriving with a plan ("Talk to our team", "Discuss this plan") means the visitor wants the demo: open the form.
  const [booking, setBooking] = useState(() => VALID_PLANS.includes(params.get('plan')));
  // Bumped by every "Book a live demo" (the choice or the end screen's): the form focuses its first field.
  const [chose, setChose] = useState(0);
  const [captured] = useState(readCapturedDetails);
  const endRef = useRef(null);
  const ended = status === 'submitted' || status === 'skipped';
  // The end screen mounts on the first ending and then stays (hidden while booking again).
  const [shown, setShown] = useState(false);
  if (ended && !shown) setShown(true);

  // The end screen opens at the top of the page; focusing it must not scroll it under the sticky nav.
  useEffect(() => {
    if (!ended) return;
    endRef.current?.focus({ preventScroll: true });
    if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
  }, [ended]);

  function openBooking() {
    setBooking(true);
    setChose((n) => n + 1);
  }

  return (
    <Screen id="next-step.book-demo" title="Next step" className={ended ? 'nsd is-ended' : 'nsd'}>
      {/* Public: the post-condition must sit outside the private section (ruling R13). */}
      {shown || ended ? (
        <EndScreen
          status={status}
          active={ended}
          panelRef={endRef}
          onBook={() => {
            setStatus('idle');
            openBooking();
          }}
        />
      ) : null}

      {/* The end screen takes over the page: the intro stays for assistive tech only. */}
      <div className={ended ? 'nsd-intro sr-only' : 'nsd-intro'}>
        <p className="eyebrow nsd-eyebrow">Next step</p>
        <h1 className="headline nsd-headline">
          See it with <em>your own reports.</em>
        </h1>
        <p className="lede nsd-lede">A short demo, built from your reports.</p>
      </div>

      <div className="nsd-layout" hidden={ended}>
        <section className="card nsd-demo" aria-labelledby="nsd-demo-title">
          <h2 id="nsd-demo-title" className="nsd-demo__title">
            What happens in the demo
          </h2>
          <ol className="nsd-points">
            {DEMO_POINTS.map(({ key, icon: Icon, tone, text }) => (
              <li key={key}>
                <span className={`nsd-tile nsd-tone--${tone}`} aria-hidden="true">
                  <Icon size={26} />
                </span>
                {text}
              </li>
            ))}
          </ol>
          <p className="pill pill--soft nsd-time">
            <ClockGlyph size={16} />
            30 minutes
          </p>
        </section>

        <div className="nsd-request-col">
          <div
            className="card nsd-choose"
            hidden={ended}
            data-trapezium-state={booking ? 'booking' : 'choosing'}
          >
            <h2 className="nsd-choose__title">Want to see it live?</h2>
            <div className="nsd-choose__actions">
              <button
                type="button"
                className={booking ? 'btn btn--primary nsd-choose__btn is-chosen' : 'btn btn--primary nsd-choose__btn'}
                aria-expanded={booking}
                aria-controls={booking ? (captured ? 'nsd-confirm' : 'nsd-form') : undefined}
                disabled={status === 'sending'}
                onClick={openBooking}
                data-trapezium-id="next-step.book-demo.choose-book"
              >
                Book a live demo
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="btn btn--ghost nsd-choose__btn"
                disabled={status === 'sending'}
                onClick={() => setStatus('skipped')}
                data-trapezium-id="next-step.book-demo.choose-skip"
              >
                Skip for now
              </button>
            </div>
          </div>

          {booking ? (
            <section
              className="nsd-request"
              data-trapezium-private="true"
              aria-label={captured ? 'Your details' : 'Demo request form'}
              hidden={ended}
            >
              {captured ? (
                <CapturedDetails details={captured} status={status} setStatus={setStatus} focusOnOpen={chose} />
              ) : (
                <DemoForm status={status} setStatus={setStatus} focusOnOpen={chose} />
              )}
            </section>
          ) : null}
        </div>
      </div>
    </Screen>
  );
}
